/* eslint-disable no-unused-expressions */
import { FC, useState, useEffect } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { CustomerType } from "../../constants/customerTypes";
import { useDebounce } from "../../hooks/useDebounce";
import { Inputs } from "../molecules";
import type {
  FilterOptionsParams,
  FilterValue,
  FilterValues,
  IFilterItem,
} from "../../services/api/models/Filter";
import type { IFilterOptions } from "../../services/api/models/FilterOptions";
import type { AutocompleteValue } from "../molecules/Inputs/AutocompleteField";
import type { DateMode } from "../molecules/Inputs/DateRange";
import type { InputSelectValue } from "../molecules/Inputs/Select";

interface FilterProps {
  customerType?: CustomerType;
  filter: IFilterItem;
  filters: FilterValues;
  setFilters: (key: string, value: FilterValue) => void;
  useFilterOptions: (
    params: FilterOptionsParams
  ) => UseQueryResult<IFilterOptions, Error>;
  updateDefaultReady?: (key: string) => void;
}
const Filter: FC<FilterProps> = ({
  filter,
  customerType,
  filters,
  setFilters,
  useFilterOptions,
  updateDefaultReady,
}) => {
  const {
    key,
    caption,
    filter_type,
    extra,
    default_value,
    multiple,
    dependency,
  } = filter;
  const [search, setSearch] = useState<string | null>("");
  const [isFirst, setIsFirst] = useState<boolean>(true);
  const dependencyObject: FilterValues = {};
  dependency.forEach((el) => {
    dependencyObject[el] = filters[el];
    if (el === "date_range_selection") {
      dependencyObject.date_range = filters?.date_range;
    }
  });

  const { data: options, isLoading } = useFilterOptions(
    useDebounce(
      {
        customerType,
        filter,
        dependencyObject,
        search,
      },
      1000
    )
  );

  const getDefaultValueByType = (): FilterValue => {
    switch (filter_type) {
      case "autocomplete": {
        const optionValue =
          typeof default_value === "string" || typeof default_value === "number"
            ? default_value
            : String(default_value ?? "");
        return { value: optionValue, caption: String(optionValue) };
      }
      default:
        return default_value;
    }
  };

  const getDefaultValue = (): FilterValue => {
    let value: FilterValue = default_value;
    if (default_value) {
      value = getDefaultValueByType();
    }
    if (multiple) {
      if (!value) {
        value = [];
      } else if (!Array.isArray(value)) {
        value = [value];
      }
    }
    if (isFirst && !extra?.select_first) {
      setIsFirst(false);
    }
    return value;
  };

  const [value, setValue] = useState<FilterValue>(
    filters[key] ?? getDefaultValue()
  );

  useEffect(() => {
    setFilters(key, value);
  }, [value]);

  /**
   * This useEffect reports readiness of the field (as soon as first value is set).
   * For filters with extra.select_first value is set as soon as options were fetched.
   **/
  useEffect(() => {
    if (!isFirst) {
      !!updateDefaultReady && updateDefaultReady(key);
    }
  }, [isFirst]);

  useEffect(() => {
    if (isFirst && extra?.select_first && (options?.length ?? 0) > 0) {
      const value = options![0];
      if (multiple) {
        setValue([value]);
      } else {
        setValue(value);
      }
      setIsFirst(false);
    }
  }, [options]);

  switch (filter_type) {
    case "autocomplete":
      return (
        <Inputs.AutocompleteField
          value={value as AutocompleteValue}
          onChange={(nextValue) => setValue(nextValue as FilterValue)}
          onSearch={setSearch}
          options={options || []}
          label={caption}
          multiple={multiple}
          loading={isLoading}
        />
      );
    case "select":
      return (
        <Inputs.Select
          value={value as InputSelectValue}
          onChange={(nextValue) => setValue(nextValue)}
          options={options || []}
          label={caption}
          fullWidth={false}
          multiple={multiple}
        />
      );
    case "date_range_selection":
      return (
        <Inputs.DateRange
          onDateChange={(value) => {
            setFilters("date_range", value);
          }}
          setDateMode={(nextValue) => setValue(nextValue)}
          value={value as DateMode}
          options={options || []}
          size="small"
          extra={extra}
        />
      );
    default:
      return (
        <Inputs.Text
          onChange={(nextValue) => setValue(nextValue)}
          label={caption}
        />
      );
  }
};

export { Filter };
